// A fila que DESTRAVA — o que faltava na tela.
//
// A trava não sai por esta tela: sai por classificação humana da verba. A versão
// anterior mostrava quantos vínculos seriam bloqueados e parava aí, o que
// transformava "trabalho ordenado por impacto" em "procurar onde está o
// problema". O servidor já agrupa as pendências por verba e ordena pelo valor
// que cada uma trava — a informação existia e não aparecia em lugar nenhum.
//
// A ORDEM VEM DO SERVIDOR e não é refeita aqui: `verbas[]` chega por valor
// decrescente, e reordenar no cliente criaria uma segunda opinião sobre qual é
// a verba mais cara.
//
// Classificar acontece NESTA TELA, num painel que hospeda o formulário do Motor
// de Rubricas (extraído para `motor-rubricas-classificar`). Reimplementá-lo aqui
// seria a terceira cópia do mesmo formulário.

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, DataTable, FilterChips, Pagination, Pill, type Column } from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import { ModalClassificacao } from "./motor-rubricas-classificar";

const brl = (n: number | null | undefined) =>
  n == null ? "—" : n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

type Verba = NonNullable<Awaited<ReturnType<typeof atlas.admin.trava.pendencias>>["verbas"]>[number];

export function TravaMargemFila({
  unidadeId,
  unidadeNome,
}: {
  unidadeId: number | null;
  unidadeNome?: string;
}) {
  const [classificando, setClassificando] = useState<Verba | null>(null);
  const [ultimoResultado, setUltimoResultado] = useState<string | null>(null);
  const [filtroCatalogo, setFiltroCatalogo] = useState("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(25);

  const pendencias = useQuery({
    queryKey: ["admin", "trava", "pendencias", unidadeId],
    queryFn: () => atlas.admin.trava.pendencias(unidadeId!),
    enabled: unidadeId != null,
  });

  const d = pendencias.data;
  const valida = d?.medicao_valida === true;
  const todas = useMemo(() => d?.verbas ?? [], [d]);

  // Recorte no cliente: a resposta traz a fila inteira da unidade, já ordenada
  // pelo servidor. O filtro separa o que resolve rápido (verba que já existe no
  // catálogo, só falta confirmar) do que exige cadastro novo.
  const filtradas = useMemo(
    () => (filtroCatalogo === "" ? todas : todas.filter((v) => String(v.no_catalogo) === filtroCatalogo)),
    [todas, filtroCatalogo],
  );
  const verbas = filtradas.slice((pagina - 1) * porPagina, pagina * porPagina);

  const colunas: Column<Verba>[] = [
    { key: "codigo", header: "Verba", mono: true, render: (v) => v.codigo_verba },
    { key: "descricao", header: "Descrição", wrap: true, render: (v) => v.descricao || "—" },
    {
      key: "valor",
      header: "Valor travado",
      align: "right",
      // É a coluna que ordena a fila. Vem primeiro entre os números de propósito.
      render: (v) => <strong>{brl(v.valor)}</strong>,
    },
    { key: "servidores", header: "Vínculos", align: "right", render: (v) => v.servidores },
    { key: "quantidade", header: "Linhas", align: "right", render: (v) => v.quantidade },
    {
      key: "consignataria",
      header: "Consignatária declarada",
      wrap: true,
      render: (v) => v.consignataria_declarada ?? "—",
    },
    {
      key: "catalogo",
      header: "No catálogo",
      render: (v) =>
        v.no_catalogo ? <Pill variant="aceita">sim</Pill> : <Pill variant="pendente">não</Pill>,
    },
  ];

  if (unidadeId == null) return null;

  return (
    <Card>
      <h2 style={{ margin: "0 0 4px", fontSize: 16 }}>Fila que destrava</h2>
      <p style={{ margin: "0 0 12px", color: "var(--text-muted)", fontSize: 13 }}>
        Cada verba classificada libera os vínculos que ela segurava — sem mexer na trava. A ordem é pelo valor
        que cada uma trava.
      </p>

      {ultimoResultado && (
        <p role="status" style={{ color: "var(--success)" }}>
          {ultimoResultado}
        </p>
      )}

      {d && !valida && (
        <p role="alert" style={{ color: "var(--danger-500)" }}>
          Não foi possível ler as pendências desta unidade{d.erro ? ` — ${d.erro}` : ""}.
        </p>
      )}

      {valida && todas.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <FilterChips
            label="No catálogo"
            value={filtroCatalogo}
            onChange={(v) => {
              setFiltroCatalogo(v);
              setPagina(1);
            }}
            options={[
              { value: "", label: "Todas", count: todas.length },
              {
                value: "true",
                label: "Já no catálogo",
                cor: "var(--emerald-500)",
                count: todas.filter((v) => v.no_catalogo).length,
              },
              {
                value: "false",
                label: "Fora do catálogo",
                cor: "var(--gold-500)",
                count: todas.filter((v) => !v.no_catalogo).length,
              },
            ]}
          />
        </div>
      )}

      {valida && todas.length === 0 ? (
        // Estado de SUCESSO, não tabela vazia: catálogo em dia é o objetivo, e a
        // frase responde a pergunta que o operador tem na mão.
        <p style={{ color: "var(--success)" }}>
          Nenhuma verba pendente de classificação{unidadeNome ? ` em ${unidadeNome}` : ""}. Ligar a trava
          agora não bloquearia nenhum vínculo.
        </p>
      ) : (
        <>
          {valida && (
            <p style={{ margin: "0 0 8px", fontSize: 13, color: "var(--text-muted)" }}>
              {todas.length} verba(s) pendente(s), somando <strong>{brl(d?.total_valor)}</strong> e travando{" "}
              <strong>{d?.vinculos_bloqueados ?? "—"}</strong> vínculo(s).
            </p>
          )}
          <DataTable
            columns={colunas}
            rows={verbas}
            rowKey={(v) => v.codigo_verba}
            loading={pendencias.isLoading}
            emptyState="Nenhuma verba com este filtro."
            actions={(v) => (
              <Button size="sm" variant="ghost" onClick={() => setClassificando(v)}>
                Classificar
              </Button>
            )}
          />
          <Pagination
            total={filtradas.length}
            pagina={pagina}
            porPagina={porPagina}
            onPagina={setPagina}
            onPorPagina={(n) => {
              setPorPagina(n);
              setPagina(1);
            }}
            rotulo="verbas"
          />
        </>
      )}

      {classificando && (
        <ModalClassificacao
          folhaId={unidadeId}
          rubrica={null}
          codigoVerba={classificando.codigo_verba}
          onClose={() => setClassificando(null)}
          onSuccess={(r) => {
            setUltimoResultado(
              `Verba ${classificando.codigo_verba} classificada.` +
                (r.descontosPromovidos > 0
                  ? ` ${r.descontosPromovidos} desconto(s) saíram da quarentena.`
                  : "") +
                (r.dividasReclassificadas > 0
                  ? ` ${r.dividasReclassificadas} dívida(s) tiveram credor/produto reapontados.`
                  : ""),
            );
          }}
          // Sem estas chaves a verba recém-classificada continuaria na fila e o
          // número de vínculos bloqueados seguiria o antigo — o operador veria a
          // tela contradizer o que acabou de fazer.
          invalidateKeys={[
            ["admin", "trava", "pendencias", unidadeId],
            ["admin", "trava", "impacto", unidadeId],
            ["admin", "trava", "monitor"],
          ]}
        />
      )}
    </Card>
  );
}
